import { useMemo, useState } from 'react'
import {
  Button,
  Chip,
  DataTable,
  DefinitionList,
  Drawer,
  Pager,
  StateBlock,
  useClientPagination,
  type Column,
} from '@w7/ui'
import { useOneAccount } from '@/app/queries'
import { Amount, Money } from '@/app/bits'
import { EMPTY, lll, odds } from '@/lib/format'
import {
  formatLineSessionDisplayRun,
  lineSessionDisplayRun,
} from '@/lib/lineSessionDisplayRun'
import type { PlStatementRowView } from '@/api/map/plStatement'
import {
  cardImageSrc,
  isCardCodeToken,
  type ArCasinoBetView,
  type CardGameMarketView,
  type CardGameRunnerView,
  type FundistActionView,
  type OneAccountBetView,
} from '@/api/map/oneAccount'
import { PlStatementGraphModal } from './PlStatementGraphModal'
import { canShowPlViewGraph, plArchiveGraphFilter } from './plViewGraph'
import './reports.css'

const EMPTY_BETS: OneAccountBetView[] = []
const EMPTY_AR_BETS: ArCasinoBetView[] = []
const EMPTY_FUNDIST: FundistActionView[] = []

function PlayingCardImg({ code }: { code: string }) {
  return (
    <img
      className="pl-playing-card"
      src={cardImageSrc(code)}
      alt={code || 'card'}
      width={42}
      height={58}
      loading="lazy"
      decoding="async"
    />
  )
}

function RunnerHand({ runner }: { runner: CardGameRunnerView }) {
  return (
    <div className="pl-card-runner">
      <div className="pl-card-kv">
        <span>Name :</span>
        <span>{runner.name || EMPTY}</span>
        <span>Status :</span>
        <span>{runner.status || EMPTY}</span>
        {runner.cards.length ? (
          <>
            <span>Card :</span>
            <div className="pl-card-hand">
              {runner.cards.map((c, i) => (
                <PlayingCardImg key={`${c}-${i}`} code={c} />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Lab sportsbook-detail «Card» block — Card Game only.
 * Three layouts: runners-only, joker/ante index, or index + runners.
 */
export function CardGameResultSection({ market }: { market: CardGameMarketView }) {
  const runners = market.marketRunner
  const index = market.indexCard
  const hasIndex = index.length > 0
  const hasRunners = runners.length > 0

  return (
    <div className="pl-card-result" data-testid="card-game-result">
      <h3 className="pl-card-result-title">Card</h3>
      {!hasIndex && hasRunners ? (
        <div className="pl-card-runners">
          {runners.map((r, i) => (
            <RunnerHand key={`${r.name}-${i}`} runner={r} />
          ))}
        </div>
      ) : null}
      {hasIndex && !hasRunners ? (
        <DefinitionList
          columns={2}
          items={[
            {
              label: 'Joker',
              value: index[0] != null ? <PlayingCardImg code={String(index[0])} /> : EMPTY,
            },
            {
              label: 'Ante',
              value: index[1] != null ? <PlayingCardImg code={String(index[1])} /> : EMPTY,
            },
          ]}
        />
      ) : null}
      {hasIndex && hasRunners ? (
        <>
          <div className="pl-card-index">
            <span className="pl-card-index-label">
              Index :{' '}
              {index
                .filter((t) => !isCardCodeToken(t))
                .map((t, i) => (
                  <span key={`n-${i}`} className="pl-card-index-num">
                    {String(t)}
                  </span>
                ))}
            </span>
            <div className="pl-card-hand">
              {index
                .filter((t) => isCardCodeToken(t))
                .map((t, i) => (
                  <PlayingCardImg key={`c-${i}`} code={String(t)} />
                ))}
            </div>
          </div>
          <div className="pl-card-runners">
            {runners.map((r, i) => (
              <RunnerHand key={`${r.name}-${i}`} runner={r} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}

/**
 * Lab `openDetailView` → `Account/getOneAccount` → Game Detail.
 *
 * SportsBook / Racing / Card Game share the sportsbook modal columns.
 * Live Casino (Evolution) uses Lab ar-roulette-detail when ARbetDetails is present.
 * Crash / Fundist uses Lab live-casino-detail (outputObject.actions).
 */
export function PlStatementDetailDrawer({
  row,
  currency,
  onClose,
}: {
  row: PlStatementRowView
  currency?: string
  onClose: () => void
}) {
  const query = useOneAccount(row)
  const detail = query.data
  const displayCurrency = detail?.currencyType ?? currency
  const isFundistDetail = Boolean(detail?.isFundist && detail.fundistDetails)
  const isCasinoDetail = Boolean(detail?.isLiveCasino && detail.arBetDetails)
  const sportsBets = detail?.bets ?? EMPTY_BETS
  const casinoBets = detail?.arBetDetails?.bets ?? EMPTY_AR_BETS
  const fundistActions = detail?.fundistDetails?.actions ?? EMPTY_FUNDIST
  const sportsPaging = useClientPagination(sportsBets, { resetKey: row.id })
  const casinoPaging = useClientPagination(casinoBets, { resetKey: `${row.id}:ar` })
  const fundistPaging = useClientPagination(fundistActions, {
    resetKey: `${row.id}:fundist`,
  })
  const [graphOpen, setGraphOpen] = useState(false)
  const showViewGraph = Boolean(detail && canShowPlViewGraph(detail))
  const graphFilter =
    graphOpen && detail ? plArchiveGraphFilter(detail, row) : null

  const viewGraphBar = showViewGraph ? (
    <div
      className="w7-row"
      style={{ gap: 'var(--w7-s3)', marginBottom: 'var(--w7-s4)', flexWrap: 'wrap' }}
      data-testid="pl-detail-actions"
    >
      <Button
        variant="secondary"
        data-testid="pl-view-graph"
        onClick={() => setGraphOpen(true)}
      >
        View Graph
      </Button>
    </div>
  ) : null

  const sportsColumns = useMemo<Column<OneAccountBetView>[]>(() => {
    const card = Boolean(detail?.isCardGame)
    const cols: Column<OneAccountBetView>[] = [
      {
        id: 'placed',
        header: 'Place Date',
        width: 168,
        cell: (bet) => <span className="w7-dim">{bet.placedAt ? lll(bet.placedAt) : EMPTY}</span>,
        sortValue: (bet) => bet.placedAt ?? '',
      },
    ]
    if (!card) {
      cols.push({
        id: 'matched',
        header: 'Matched Date',
        width: 168,
        hideBelow: 900,
        cell: (bet) => <span className="w7-dim">{bet.matchedAt ? lll(bet.matchedAt) : EMPTY}</span>,
        sortValue: (bet) => bet.matchedAt ?? '',
      })
    }
    cols.push({
      id: 'type',
      header: 'Type',
      width: 76,
      cell: (bet) => (
        <Chip size="sm" tone={bet.type === 'LAY' ? 'lay' : 'back'}>
          {bet.type === 'LAY' ? 'Lay' : 'Back'}
        </Chip>
      ),
      sortValue: (bet) => bet.type,
    })
    if (!card) {
      cols.push({
        id: 'status',
        header: 'Status',
        cell: (bet) => bet.status || EMPTY,
        sortValue: (bet) => bet.status,
      })
    }
    const showRun =
      Boolean(detail?.isLineSession) || sportsBets.some((b) => b.runs != null)
    cols.push({
      id: 'selection',
      header: 'Horse Name',
      cell: (bet) => bet.selectionName || EMPTY,
      sortValue: (bet) => bet.selectionName,
    })
    if (showRun) {
      cols.push({
        id: 'runs',
        header: 'Run',
        numeric: true,
        cell: (bet) =>
          bet.runs != null
            ? formatLineSessionDisplayRun(bet.runs, bet.type) || EMPTY
            : EMPTY,
        sortValue: (bet) =>
          bet.runs != null ? lineSessionDisplayRun(bet.runs, bet.type) : bet.runs,
      })
    }
    cols.push(
      {
        id: 'odds',
        header: 'Odds',
        numeric: true,
        cell: (bet) => (bet.odds != null ? odds(bet.odds) : EMPTY),
        sortValue: (bet) => bet.odds,
      },
      {
        id: 'avgOdds',
        header: 'Avg. Odds',
        numeric: true,
        hideBelow: 1100,
        cell: (bet) => (bet.avgOdds != null ? odds(bet.avgOdds) : EMPTY),
        sortValue: (bet) => bet.avgOdds,
      },
      {
        id: 'stake',
        header: 'Stake',
        numeric: true,
        cell: (bet) => <Amount value={bet.stake} currency={displayCurrency} />,
        sortValue: (bet) => bet.stake,
      },
      {
        id: 'matchedStake',
        header: 'Matched Stake',
        numeric: true,
        hideBelow: 900,
        cell: (bet) => <Amount value={bet.matchedStake} currency={displayCurrency} />,
        sortValue: (bet) => bet.matchedStake,
      },
      {
        id: 'pnl',
        header: 'Profit/Loss',
        numeric: true,
        cell: (bet) =>
          bet.pnl == null ? EMPTY : <Money value={bet.pnl} signed currency={displayCurrency} />,
        sortValue: (bet) => bet.pnl,
      },
    )
    return cols
  }, [displayCurrency, detail?.isCardGame, detail?.isLineSession, sportsBets])

  const casinoColumns = useMemo<Column<ArCasinoBetView>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        cell: (bet) => bet.code || EMPTY,
        sortValue: (bet) => bet.code,
      },
      {
        id: 'description',
        header: 'Description',
        cell: (bet) => bet.description || EMPTY,
        sortValue: (bet) => bet.description,
      },
      {
        id: 'stake',
        header: 'Stake',
        numeric: true,
        cell: (bet) => <Amount value={bet.stake} currency={displayCurrency} />,
        sortValue: (bet) => bet.stake,
      },
      {
        id: 'payout',
        header: 'Payout',
        numeric: true,
        cell: (bet) => <Amount value={bet.payout} currency={displayCurrency} />,
        sortValue: (bet) => bet.payout,
      },
    ],
    [displayCurrency],
  )

  const fundistColumns = useMemo<Column<FundistActionView>[]>(
    () => [
      {
        id: 'actionTime',
        header: 'Action Time',
        width: 168,
        cell: (a) => a.actionTime || EMPTY,
        sortValue: (a) => a.actionTime,
      },
      {
        id: 'actionAmount',
        header: 'Action Amount',
        numeric: true,
        cell: (a) => <Amount value={a.actionAmount} currency={displayCurrency} />,
        sortValue: (a) => a.actionAmount,
      },
      {
        id: 'bonus',
        header: 'Action Amount Bonus',
        numeric: true,
        hideBelow: 900,
        cell: (a) => <Amount value={a.actionAmountBonus} currency={displayCurrency} />,
        sortValue: (a) => a.actionAmountBonus,
      },
      {
        id: 'real',
        header: 'Action Amount Real',
        numeric: true,
        hideBelow: 900,
        cell: (a) => <Amount value={a.actionAmountReal} currency={displayCurrency} />,
        sortValue: (a) => a.actionAmountReal,
      },
      {
        id: 'actionId',
        header: 'Action Id',
        cell: (a) => a.actionId || EMPTY,
        sortValue: (a) => a.actionId,
      },
      {
        id: 'type',
        header: 'Action Type',
        cell: (a) => a.actionType || EMPTY,
        sortValue: (a) => a.actionType,
      },
    ],
    [displayCurrency],
  )

  const title = detail?.eventName || row.eventName
  const subtitle = `${detail?.gameName ?? row.game} · ${detail?.subGame ?? row.sport} · ${detail?.marketName ?? row.marketName}`
  const ar = detail?.arBetDetails
  const fundist = detail?.fundistDetails
  const outcome = ar?.outcomes[0]

  const activePaging = isFundistDetail
    ? fundistPaging
    : isCasinoDetail
      ? casinoPaging
      : sportsPaging

  return (
    <Drawer
      open
      onClose={onClose}
      width="half"
      title="Game Detail"
      subtitle={`${title} — ${subtitle}`}
      footer={
        activePaging.total > activePaging.pageSize ? (
          <Pager
            page={activePaging.page}
            pageCount={activePaging.pageCount}
            onChange={activePaging.setPage}
            total={activePaging.total}
            pageSize={activePaging.pageSize}
          />
        ) : undefined
      }
    >
      {query.isLoading ? (
        <StateBlock icon="info" title="Loading, please wait…">
          Fetching this settlement’s bets from Account/getOneAccount.
        </StateBlock>
      ) : detail?.unavailable === 'forbidden' ? (
        <StateBlock icon="lock" title="Unauthorised access">
          This settlement is not in your hierarchy.
        </StateBlock>
      ) : detail?.unavailable === 'live-casino' ? (
        <StateBlock icon="info" title="Live casino detail is not available">
          Lab opened a Kings AR or Fundist Crash modal here. Detail payload was not
          returned for this settlement.
        </StateBlock>
      ) : detail?.unavailable === 'empty' ? (
        <StateBlock icon="info" title="No bet detail found">
          Account/getOneAccount returned no document for this row.
        </StateBlock>
      ) : isFundistDetail && fundist ? (
        <>
          <DefinitionList
            columns={2}
            items={[
              {
                label: 'Game Date',
                value: detail?.resultTime ? lll(detail.resultTime) : EMPTY,
              },
              { label: 'Player Name', value: detail?.username ?? row.memberName },
              { label: 'Game Type', value: fundist.gameDesc || detail?.subGame || EMPTY },
              { label: 'Game ID', value: fundist.gameId || EMPTY },
              { label: 'Game Start Time', value: fundist.startTime || EMPTY },
              { label: 'Game End Time', value: fundist.endTime || EMPTY },
              { label: 'Win/Loss', value: detail?.winLoss || EMPTY },
              {
                label: 'Win/Loss Amt',
                value: <Money value={detail?.netAmount ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: 'Commission Amt',
                value: <Amount value={detail?.commissionAmt ?? 0} currency={displayCurrency} />,
              },
            ]}
          />
          <div style={{ marginTop: 'var(--w7-s5)' }}>
            <DataTable
              label={`Bets for ${detail?.username ?? row.memberName}`}
              rows={fundistPaging.rows}
              columns={fundistColumns}
              rowKey={(a) => a.id}
              empty={
                <StateBlock icon="info" title="No Fundist actions">
                  SoftGamings GameDetails had no actions for this round.
                </StateBlock>
              }
            />
          </div>
        </>
      ) : isCasinoDetail && ar ? (
        <>
          <DefinitionList
            columns={2}
            items={[
              {
                label: 'Game Date',
                value: detail?.resultTime ? lll(detail.resultTime) : EMPTY,
              },
              { label: 'Player Name', value: detail?.username ?? row.memberName },
              { label: 'Game Type', value: ar.tableName || ar.gameType || EMPTY },
              { label: 'Win/Loss', value: detail?.winLoss || EMPTY },
              {
                label: 'Win/Loss Amt',
                value: <Money value={detail?.netAmount ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: 'Commission Amt',
                value: <Amount value={detail?.commissionAmt ?? 0} currency={displayCurrency} />,
              },
            ]}
          />
          {outcome ? (
            <div style={{ marginTop: 'var(--w7-s5)' }}>
              <DefinitionList
                columns={3}
                items={[
                  { label: 'Number', value: outcome.number || EMPTY },
                  { label: 'Type', value: outcome.type || EMPTY },
                  { label: 'Color', value: outcome.color || EMPTY },
                ]}
              />
            </div>
          ) : null}
          {ar.transactionId ? (
            <p className="w7-dim" style={{ marginTop: 'var(--w7-s4)' }}>
              Transaction Id: {ar.transactionId}
            </p>
          ) : null}
          <div style={{ marginTop: 'var(--w7-s5)' }}>
            <DataTable
              label={`Bets for ${detail?.username ?? row.memberName}`}
              rows={casinoPaging.rows}
              columns={casinoColumns}
              rowKey={(bet) => bet.id}
              empty={
                <StateBlock icon="info" title="No casino bets">
                  ARbetDetails had no participant bets for this round.
                </StateBlock>
              }
            />
          </div>
        </>
      ) : (
        <>
          {viewGraphBar}
          {detail?.isRollback ? (
            <p className="w7-dim" style={{ marginBottom: 'var(--w7-s4)' }}>
              Rollback Bets
            </p>
          ) : null}
          <DefinitionList
            columns={2}
            items={[
              {
                label: 'Game Date',
                value: detail?.resultTime ? lll(detail.resultTime) : EMPTY,
              },
              { label: 'Player Name', value: detail?.username ?? row.memberName },
              { label: 'Game Type', value: detail?.subGame || row.sport },
              { label: 'Win/Loss', value: detail?.winLoss || EMPTY },
              {
                label: 'Win Amt',
                value: <Money value={detail?.winAmt ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: detail?.subGame === 'Horse Racing' ? 'Deduction Amt' : 'Commission Amt',
                value: <Amount value={detail?.commissionAmt ?? 0} currency={displayCurrency} />,
              },
              {
                label: 'Net Amt',
                value: <Money value={detail?.netAmount ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: 'Round Id',
                value: detail?.roundId || EMPTY,
              },
              { label: 'Winner Name', value: detail?.winner || EMPTY },
              { label: 'Match', value: detail?.eventName || row.eventName },
            ]}
          />
          <div style={{ marginTop: 'var(--w7-s5)' }}>
            <DataTable
              label={`Bets for ${detail?.username ?? row.memberName}`}
              rows={sportsPaging.rows}
              columns={sportsColumns}
              rowKey={(bet) => bet.id}
              empty={
                <StateBlock icon="info" title="No executed bets">
                  Lab only lists bets with amountExecuted or rtAmountExecuted greater than zero.
                </StateBlock>
              }
            />
          </div>
          {detail?.isCardGame && detail.cardMarkets[0] ? (
            <CardGameResultSection market={detail.cardMarkets[0]} />
          ) : null}
        </>
      )}
      {graphOpen ? (
        <PlStatementGraphModal
          open={graphOpen}
          filter={graphFilter}
          eventName={detail?.eventName || row.eventName}
          onClose={() => setGraphOpen(false)}
        />
      ) : null}
    </Drawer>
  )
}
